import type {
  WorkOrderLetterAddress,
  WorkOrderPrintMode,
  WorkOrderPrintModeOption,
  WorkOrderPrintPreviewRow,
  WorkOrderPrintRecipientOption,
} from "../../../../../../../../../../../../../api/kehrblatt/letters"

const recordValue = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null

const hasOnlyKeys = (value: Record<string, unknown>, allowed: readonly string[]) =>
  Object.keys(value).every((key) => allowed.includes(key))

const requiredString = (value: unknown): string | null => {
  if (typeof value !== "string") return null

  const normalized = value.trim()

  return normalized || null
}

const optionalString = (value: unknown): string | null =>
  value == null ? null : requiredString(value)

const optionalBoolean = (value: unknown): boolean | null =>
  typeof value === "boolean" ? value : null

const optionalNumber = (value: unknown): number | null =>
  typeof value === "number" && Number.isFinite(value) ? value : null

const stringList = (value: unknown): string[] | null => {
  if (!Array.isArray(value)) return null

  const result: string[] = []
  for (const entry of value) {
    const normalized = requiredString(entry)

    if (!normalized) return null

    result.push(normalized)
  }

  return result
}

export const previewPrintMode = (value: unknown): WorkOrderPrintMode | null => {
  if (
    value === "PERSONAL_LETTER" ||
    value === "IMPERSONAL_LETTER" ||
    value === "GENERIC_KLEBEZETTEL"
  ) {
    return value
  }

  return null
}

export const parseWorkOrderLetterAddress = (value: unknown): WorkOrderLetterAddress | null => {
  if (value == null) return null

  const source = recordValue(value)

  if (!source) return null

  const optionalFields = [
    "street",
    "house_number",
    "zip",
    "city",
    "country",
    "address_extra",
  ] as const

  if (
    !hasOnlyKeys(source, ["label", "lines", ...optionalFields]) ||
    optionalFields.some((field) => source[field] != null && !requiredString(source[field]))
  ) {
    return null
  }

  const label = requiredString(source.label)
  const lines = stringList(source.lines)

  if (label == null || lines == null) return null

  return {
    label,
    lines,
    street: optionalString(source.street),
    house_number: optionalString(source.house_number),
    zip: optionalString(source.zip),
    city: optionalString(source.city),
    country: optionalString(source.country),
    address_extra: optionalString(source.address_extra),
  }
}

const parseModeOption = (value: unknown): WorkOrderPrintModeOption | null => {
  const source = recordValue(value)

  if (!source || !hasOnlyKeys(source, ["mode", "label", "disabled_reason"])) return null

  if (source.disabled_reason != null && !requiredString(source.disabled_reason)) return null

  const mode = previewPrintMode(source.mode)
  const label = requiredString(source.label)

  if (!mode || !label) return null

  return {
    mode,
    label,
    disabled_reason: optionalString(source.disabled_reason),
  }
}

const parseRecipientOption = (value: unknown): WorkOrderPrintRecipientOption | null => {
  const source = recordValue(value)
  const optionalFields = ["sub_label", "role", "role_label", "address_label"] as const

  if (
    !source ||
    !hasOnlyKeys(source, [
      "customer_id",
      "contact_point_id",
      "label",
      ...optionalFields,
      "postal_address",
    ]) ||
    optionalFields.some((field) => source[field] != null && !requiredString(source[field]))
  ) {
    return null
  }

  const customerId = requiredString(source.customer_id)
  const contactPointId = requiredString(source.contact_point_id)
  const label = requiredString(source.label)

  if (!customerId || !contactPointId || !label) return null

  const postalAddress =
    source.postal_address == null ? null : parseWorkOrderLetterAddress(source.postal_address)

  if (source.postal_address != null && !postalAddress) return null

  return {
    customer_id: customerId,
    contact_point_id: contactPointId,
    label,
    sub_label: optionalString(source.sub_label),
    role: optionalString(source.role),
    role_label: optionalString(source.role_label),
    address_label: optionalString(source.address_label),
    postal_address: postalAddress,
  }
}

const parseList = <Value>(
  value: unknown,
  parse: (entry: unknown) => Value | null,
): Value[] | null => {
  if (!Array.isArray(value)) return null

  const result: Value[] = []
  for (const entry of value) {
    const parsed = parse(entry)

    if (!parsed) return null

    result.push(parsed)
  }

  return result
}

const parseMarkableStep = (value: unknown): WorkOrderPrintPreviewRow["markable_step"] => {
  if (value == null) return null

  const source = recordValue(value)

  if (
    !source ||
    !hasOnlyKeys(source, [
      "case_id",
      "contact_order_id",
      "contact_order_step_id",
      "label",
      "status",
    ]) ||
    (source.status != null && !requiredString(source.status))
  ) {
    return undefined
  }

  const caseId = requiredString(source.case_id)
  const contactOrderId = requiredString(source.contact_order_id)
  const contactOrderStepId = requiredString(source.contact_order_step_id)
  const label = requiredString(source.label)

  if (!caseId || !contactOrderId || !contactOrderStepId || !label) return undefined

  return {
    case_id: caseId,
    contact_order_id: contactOrderId,
    contact_order_step_id: contactOrderStepId,
    label,
    status: optionalString(source.status),
  }
}

export const parseWorkOrderPrintPreviewRow = (value: unknown): WorkOrderPrintPreviewRow | null => {
  const source = recordValue(value)

  if (!source) return null

  const optionalStringFields = [
    "print_group_id",
    "property_id",
    "property_unit_id",
    "unit_label",
    "property_unit_type",
    "order_number",
    "order_type_internal_label",
    "work_order_label",
    "work_order_sub_label",
    "appointment_start_at",
    "appointment_end_at",
    "appointment_label",
    "announcement_label",
    "selected_recipient_customer_id",
    "selected_recipient_contact_point_id",
    "search_text",
    "disabled_reason",
  ] as const
  const optionalBooleanFields = [
    "has_residential_units",
    "is_multi_unit_property",
    "supports_generic_klebezettel",
  ] as const

  if (
    !hasOnlyKeys(source, [
      "work_order_id",
      "property_label",
      "property_address",
      "work_order_reference",
      "markable_step",
      "default_mode",
      "available_modes",
      "recipient_options",
      "property_unit_count",
      ...optionalStringFields,
      ...optionalBooleanFields,
    ]) ||
    optionalStringFields.some((field) => source[field] != null && !requiredString(source[field])) ||
    optionalBooleanFields.some(
      (field) => source[field] != null && typeof source[field] !== "boolean",
    ) ||
    (source.property_unit_count != null && optionalNumber(source.property_unit_count) == null) ||
    (source.work_order_reference != null && !recordValue(source.work_order_reference))
  ) {
    return null
  }

  const workOrderId = requiredString(source.work_order_id)
  const propertyLabel = requiredString(source.property_label)
  const defaultMode = previewPrintMode(source.default_mode)
  const availableModes = parseList(source.available_modes, parseModeOption)
  const recipientOptions = parseList(source.recipient_options, parseRecipientOption)
  const propertyAddress =
    source.property_address == null ? null : parseWorkOrderLetterAddress(source.property_address)
  const markableStep = parseMarkableStep(source.markable_step)

  if (
    !workOrderId ||
    !propertyLabel ||
    !defaultMode ||
    !availableModes ||
    !recipientOptions ||
    (source.property_address != null && !propertyAddress) ||
    markableStep === undefined
  ) {
    return null
  }

  if (!availableModes.some((option) => option.mode === defaultMode)) return null

  const selectedCustomerId = optionalString(source.selected_recipient_customer_id)
  const selectedContactPointId = optionalString(source.selected_recipient_contact_point_id)

  if (Boolean(selectedCustomerId) !== Boolean(selectedContactPointId)) return null

  if (
    selectedCustomerId &&
    !recipientOptions.some(
      (option) =>
        option.customer_id === selectedCustomerId &&
        option.contact_point_id === selectedContactPointId,
    )
  ) {
    return null
  }

  return {
    work_order_id: workOrderId,
    work_order_reference:
      source.work_order_reference == null ? null : recordValue(source.work_order_reference),
    print_group_id: optionalString(source.print_group_id),
    property_id: optionalString(source.property_id),
    property_unit_id: optionalString(source.property_unit_id),
    unit_label: optionalString(source.unit_label),
    property_unit_type: optionalString(source.property_unit_type),
    has_residential_units: optionalBoolean(source.has_residential_units),
    is_multi_unit_property: optionalBoolean(source.is_multi_unit_property),
    property_unit_count: optionalNumber(source.property_unit_count),
    supports_generic_klebezettel: optionalBoolean(source.supports_generic_klebezettel),
    order_number: optionalString(source.order_number),
    order_type_internal_label: optionalString(source.order_type_internal_label),
    work_order_label: optionalString(source.work_order_label),
    work_order_sub_label: optionalString(source.work_order_sub_label),
    property_label: propertyLabel,
    property_address: propertyAddress,
    appointment_start_at: optionalString(source.appointment_start_at),
    appointment_end_at: optionalString(source.appointment_end_at),
    appointment_label: optionalString(source.appointment_label),
    announcement_label: optionalString(source.announcement_label),
    default_mode: defaultMode,
    available_modes: availableModes,
    recipient_options: recipientOptions,
    selected_recipient_customer_id: selectedCustomerId,
    selected_recipient_contact_point_id: selectedContactPointId,
    markable_step: markableStep,
    search_text: optionalString(source.search_text),
    disabled_reason: optionalString(source.disabled_reason),
  }
}

export const previewRecord = (value: unknown): Record<string, unknown> => {
  const parsed = recordValue(value)

  if (!parsed) throw new Error("Ungültiger Step-Preview-Payload.")

  return parsed
}

export const previewRequiredString = requiredString
export const previewOptionalString = optionalString

export const previewStringMap = (value: unknown): Record<string, string> => {
  const source = recordValue(value)

  if (!source) throw new Error("Ungültige Step-Preview-Fehlerzuordnung.")

  const result: Record<string, string> = {}
  for (const [key, entry] of Object.entries(source)) {
    const message = requiredString(entry)

    if (!message) throw new Error("Ungültiger Step-Preview-Fehlertext.")

    result[key] = message
  }

  return result
}
